/**
 * ⌘K command palette. Jump-to-route + global actions. Static seed list;
 * future iterations can hydrate from real accounts/buckets via react-query.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Kbd, Mono } from './brand';

type IconName = keyof typeof Icon;

interface RouteItem {
  id: string;
  group: string;
  icon: IconName;
  label: string;
  route: string;
}
interface ActionItem {
  id: 'toggle-theme' | 'toggle-density';
  group: string;
  icon: IconName;
  label: string;
}
type Item = RouteItem | ActionItem;

const ROUTE_ITEMS: RouteItem[] = [
  {
    id: 'go-dash',
    group: 'Navigate',
    icon: 'dashboard',
    label: 'Go to Dashboard',
    route: '/dashboard',
  },
  {
    id: 'go-accts',
    group: 'Navigate',
    icon: 'accounts',
    label: 'Go to Accounts',
    route: '/accounts',
  },
  {
    id: 'go-activity',
    group: 'Navigate',
    icon: 'activity',
    label: 'Recent activity',
    route: '/activity',
  },
  {
    id: 'go-settings',
    group: 'Navigate',
    icon: 'settings',
    label: 'Admin settings',
    route: '/admin/settings',
  },
  { id: 'go-audit', group: 'Navigate', icon: 'audit', label: 'Audit log', route: '/admin/audit' },
  { id: 'go-keys', group: 'Navigate', icon: 'key', label: 'API keys', route: '/admin/api-keys' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onToggleTheme: () => void;
  onToggleDensity: () => void;
}

export function CommandPalette({ open, onClose, onToggleTheme, onToggleDensity }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const items = useMemo<Item[]>(
    () => [
      ...ROUTE_ITEMS,
      { id: 'toggle-theme', group: 'Preferences', icon: 'moon', label: 'Toggle theme' },
      { id: 'toggle-density', group: 'Preferences', icon: 'list', label: 'Toggle compact density' },
    ],
    [],
  );

  useEffect(() => {
    if (open) {
      setQ('');
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const lc = q.trim().toLowerCase();
    if (!lc) return items;
    return items.filter(
      (it) => it.label.toLowerCase().includes(lc) || it.group.toLowerCase().includes(lc),
    );
  }, [q, items]);

  const grouped = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const it of filtered) {
      const list = map.get(it.group) ?? [];
      list.push(it);
      map.set(it.group, list);
    }
    return map;
  }, [filtered]);

  if (!open) return null;

  const run = (item: Item | undefined) => {
    if (!item) return;
    if ('route' in item) {
      navigate(item.route);
    } else if (item.id === 'toggle-theme') {
      onToggleTheme();
    } else if (item.id === 'toggle-density') {
      onToggleDensity();
    }
    onClose();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(filtered[activeIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  let running = -1;

  return (
    <div
      className="bm-cmd-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bm-cmd-panel" role="dialog" aria-label="Command palette" onKeyDown={onKey}>
        <div className="bm-cmd-input-row">
          <Icon.search size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Search accounts, buckets, actions…"
            className="bm-cmd-input"
          />
          <Kbd>esc</Kbd>
        </div>
        <div className="bm-cmd-list">
          {filtered.length === 0 && (
            <div
              style={{
                padding: 24,
                color: 'var(--bm-text-muted)',
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              No matches for <Mono>{q}</Mono>
            </div>
          )}
          {[...grouped.entries()].map(([group, list]) => (
            <div key={group}>
              <div className="bm-cmd-group">{group}</div>
              {list.map((it) => {
                running += 1;
                const IconCmp = Icon[it.icon];
                const isActive = running === activeIdx;
                return (
                  <button
                    key={it.id}
                    type="button"
                    className="bm-cmd-item"
                    data-active={isActive}
                    onMouseEnter={() => setActiveIdx(filtered.indexOf(it))}
                    onClick={() => run(it)}
                  >
                    <IconCmp size={14} />
                    <span style={{ flex: 1, textAlign: 'left' }}>{it.label}</span>
                    {'route' in it && <Kbd>↩</Kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="bm-cmd-foot">
          <span
            className="bm-row bm-gap-12"
            style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}
          >
            <span className="bm-row bm-gap-4">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>navigate
            </span>
            <span className="bm-row bm-gap-4">
              <Kbd>↩</Kbd>open
            </span>
            <span className="bm-row bm-gap-4">
              <Kbd>esc</Kbd>close
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
